import { useEffect, useRef, useState } from "react";
import { useAppState } from "../AppStateContext";
import "./ScanIntroOverlay.css";

type Phase = "enter" | "exit";

export function ScanIntroOverlay() {
  const { scanIntroToken } = useAppState();
  const [lastSeenToken, setLastSeenToken] = useState(0);
  const [activeToken, setActiveToken] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>("enter");
  const videoRef = useRef<HTMLVideoElement | null>(null);

  if (scanIntroToken !== lastSeenToken) {
    setLastSeenToken(scanIntroToken);
    if (scanIntroToken > 0) {
      setActiveToken(scanIntroToken);
      setPhase("enter");
    }
  }

  useEffect(() => {
    if (activeToken === null) return;
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // Autoplay blocked — bail and dismiss immediately.
        setPhase("exit");
      });
    }
    // Safety nets so this full-screen overlay can never get stuck covering the
    // app (it sits at z-index 9999 with pointer-events). Some contexts leave
    // play() pending without resolving or rejecting: if the video hasn't
    // started shortly, dismiss; and cap the whole intro regardless.
    const startCheck = window.setTimeout(() => {
      if (v.paused && !v.ended) setPhase("exit");
    }, 1200);
    const hardCap = window.setTimeout(() => setPhase("exit"), 9000);
    return () => {
      window.clearTimeout(startCheck);
      window.clearTimeout(hardCap);
    };
  }, [activeToken]);

  // Unmount once the exit begins, without relying only on onAnimationEnd: in
  // some contexts that event never fires, leaving the overlay stuck at opacity
  // 0 but still covering the app (z-index 9999, pointer-events) and swallowing
  // every click. The timer guarantees teardown just after the exit animation.
  useEffect(() => {
    if (activeToken === null || phase !== "exit") return;
    const id = window.setTimeout(() => setActiveToken(null), 450);
    return () => window.clearTimeout(id);
  }, [phase, activeToken]);

  if (activeToken === null) return null;

  return (
    <div
      className={`scan-intro scan-intro--${phase}`}
      onAnimationEnd={(e) => {
        if (phase === "exit" && e.target === e.currentTarget) {
          setActiveToken(null);
        }
      }}
      aria-hidden
    >
      <video
        key={activeToken}
        ref={videoRef}
        className="scan-intro__video"
        src="/start.mp4"
        muted
        playsInline
        autoPlay
        preload="auto"
        onEnded={() => setPhase("exit")}
        onError={() => setPhase("exit")}
      />
    </div>
  );
}
